/**
 * Review → Punch Requests.
 * Supervisors/managers approve or deny guard-submitted punch requests.
 * Access is enforced by RLS plus the server function's role check.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { format } from "date-fns";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  PUNCH_REQUEST_KIND_LABEL,
  PUNCH_REQUEST_STATUS_LABEL,
  punchRequestStatusTone,
  type PunchRequestKind,
  type PunchRequestStatus,
} from "@/lib/punch-requests";
import {
  decidePunchRequest,
  listPunchRequestsForReview,
} from "@/lib/punch-requests.functions";

export const Route = createFileRoute("/_authenticated/review/punch-requests")({
  component: PunchRequestsReview,
  head: () => ({
    meta: [
      { title: "Punch Requests — MissionReady" },
      {
        name: "description",
        content: "Approve or deny guard requests for missed punches, corrections, and unscheduled shifts.",
      },
    ],
  }),
});

/** ISO timestamp -> value a datetime-local input accepts (local time). */
function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** datetime-local value -> ISO timestamp, or null when blank/invalid. */
function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const FILTERS = ["pending", "approved", "denied", "all"] as const;
type Filter = (typeof FILTERS)[number];

function PunchRequestsReview() {
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id ?? "";
  const qc = useQueryClient();
  const list = useServerFn(listPunchRequestsForReview);
  const decide = useServerFn(decidePunchRequest);
  const [filter, setFilter] = useState<Filter>("pending");
  const [notes, setNotes] = useState<Record<string, string>>({});
  // Reviewer-edited times, keyed by request id. Untouched requests keep the
  // guard's submitted times.
  const [edits, setEdits] = useState<Record<string, { in: string; out: string }>>({});
  const [editing, setEditing] = useState<Record<string, boolean>>({});

  const editFor = (r: any) =>
    edits[r.id] ?? { in: toLocalInput(r.requested_in_at), out: toLocalInput(r.requested_out_at) };
  const setEdit = (id: string, patch: Partial<{ in: string; out: string }>, base: { in: string; out: string }) =>
    setEdits((e) => ({ ...e, [id]: { ...base, ...patch } }));

  const q = useQuery({
    queryKey: ["punch-requests", "review", orgId, filter],
    queryFn: () => list({ data: { orgId, status: filter } }),
    enabled: !!orgId,
  });

  const mutation = useMutation({
    mutationFn: (v: {
      requestId: string;
      approve: boolean;
      note?: string;
      overrideInAt?: string | null;
      overrideOutAt?: string | null;
    }) => decide({ data: { orgId, ...v } }),
    onSuccess: async (_r, v) => {
      toast.success(v.approve ? "Request approved" : "Request denied");
      await qc.invalidateQueries({ queryKey: ["punch-requests"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not save that decision."),
  });

  if (!q.data?.allowed && q.isFetched) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-10 text-center text-sm text-[#999999]">
        You don't have permission to review punch requests.
      </div>
    );
  }

  const requests = (q.data?.requests ?? []) as any[];
  const canDecide = !!q.data?.canDecide;

  return (
    <div>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Filter punch requests">
        {FILTERS.map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? "default" : "outline"}
            onClick={() => setFilter(f)}
            aria-pressed={filter === f}
          >
            {f === "all" ? "All" : PUNCH_REQUEST_STATUS_LABEL[f as PunchRequestStatus]}
          </Button>
        ))}
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-white/10 bg-white/[0.02]">
        {q.isLoading ? (
          <div className="px-4 py-10 text-center text-sm text-[#999999]">Loading…</div>
        ) : requests.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-[#999999]">
            No punch requests to show.
          </div>
        ) : (
          <ul className="divide-y divide-white/10">
            {requests.map((r) => (
              <li key={r.id} className="px-4 py-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-white">{r.guardName}</span>
                  <span className="text-xs text-[#999999]">{r.siteName}</span>
                  <span className="text-xs text-[#C0C0C0]">
                    {PUNCH_REQUEST_KIND_LABEL[r.kind as PunchRequestKind] ?? r.kind}
                  </span>
                  <span
                    className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] ${punchRequestStatusTone(r.status)}`}
                  >
                    {PUNCH_REQUEST_STATUS_LABEL[r.status as PunchRequestStatus] ?? r.status}
                  </span>
                  {r.suggested_by_geofence && (
                    <span className="rounded-full border border-[#7CC0E8]/40 bg-[#7CC0E8]/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] text-[#7CC0E8]">
                      In perimeter
                    </span>
                  )}
                </div>
                <div className="mt-1 font-mono text-xs text-[#999999]">
                  {r.requested_in_at && `In ${format(new Date(r.requested_in_at), "MMM d · HH:mm")}`}
                  {r.requested_out_at && ` · Out ${format(new Date(r.requested_out_at), "MMM d · HH:mm")}`}
                  {` · sent ${format(new Date(r.created_at), "MMM d · HH:mm")}`}
                </div>
                <p className="mt-1 text-sm text-white/80">{r.reason}</p>
                {r.review_note && (
                  <p className="mt-1 text-xs text-[#C0C0C0]">Review note: {r.review_note}</p>
                )}

                {r.status === "pending" && canDecide && (
                  <div className="mt-3 space-y-2">
                    {editing[r.id] ? (
                      <div className="grid gap-2 sm:grid-cols-2">
                        <label className="text-xs text-[#C0C0C0]">
                          Clock in
                          <Input
                            type="datetime-local"
                            value={editFor(r).in}
                            onChange={(e) => setEdit(r.id, { in: e.target.value }, editFor(r))}
                            className="mt-1"
                          />
                        </label>
                        <label className="text-xs text-[#C0C0C0]">
                          Clock out
                          <Input
                            type="datetime-local"
                            value={editFor(r).out}
                            onChange={(e) => setEdit(r.id, { out: e.target.value }, editFor(r))}
                            className="mt-1"
                          />
                          <span className="mt-1 block text-[10px] text-[#999999]">
                            {r.kind === "unscheduled_shift"
                              ? "Called-in shifts start open — hours come from the real clock out."
                              : "Leave blank to keep the shift open."}
                          </span>
                        </label>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing((v) => ({ ...v, [r.id]: true }))}
                      >
                        Edit times before approving
                      </Button>
                    )}
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Input
                      value={notes[r.id] ?? ""}
                      onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                      placeholder="Review note (optional)"
                      aria-label={`Review note for ${r.guardName}`}
                      className="sm:flex-1"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={mutation.isPending}
                        onClick={() =>
                          mutation.mutate({
                            requestId: r.id,
                            approve: true,
                            note: notes[r.id],
                            overrideInAt: fromLocalInput(editFor(r).in),
                            overrideOutAt: fromLocalInput(editFor(r).out),
                          })
                        }
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={mutation.isPending}
                        onClick={() =>
                          mutation.mutate({ requestId: r.id, approve: false, note: notes[r.id] })
                        }
                      >
                        Deny
                      </Button>
                    </div>
                  </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
